// =====================================================
// ODONTOGRAM ADULT - Odontograma de adulto (32 dientes)
// Basado en el diseño premium proporcionado
// =====================================================
import type { SelectionMode } from './OdontogramModal';

interface OdontogramAdultProps {
  selectedTeeth: Set<string>;
  onToothClick: (toothNumber: string) => void;
  selectionMode: SelectionMode;
}

// SVG paths para diferentes tipos de dientes
const TOOTH_PATHS = {
  molar: 'M5,15 Q5,5 15,5 L25,5 Q35,5 35,15 L35,35 Q35,45 20,45 Q5,45 5,35 Z',
  premolar: 'M8,18 Q8,8 20,8 Q32,8 32,18 L32,38 Q32,45 20,45 Q8,45 8,38 Z',
  canine: 'M12,20 Q20,2 28,20 L28,40 Q28,45 20,45 Q12,45 12,40 Z',
  incisor: 'M10,15 L30,15 L30,40 Q30,46 20,46 Q10,46 10,40 Z',
};

// Configuración de dientes por posición
const TEETH_CONFIG: Record<string, { type: keyof typeof TOOTH_PATHS }> = {
  // Cuadrante 1 (Superior derecho)
  '18': { type: 'molar' },
  '17': { type: 'molar' },
  '16': { type: 'molar' },
  '15': { type: 'premolar' },
  '14': { type: 'premolar' },
  '13': { type: 'canine' },
  '12': { type: 'incisor' },
  '11': { type: 'incisor' },
  // Cuadrante 2 (Superior izquierdo)
  '21': { type: 'incisor' },
  '22': { type: 'incisor' },
  '23': { type: 'canine' },
  '24': { type: 'premolar' },
  '25': { type: 'premolar' },
  '26': { type: 'molar' },
  '27': { type: 'molar' },
  '28': { type: 'molar' },
  // Cuadrante 3 (Inferior izquierdo)
  '31': { type: 'incisor' },
  '32': { type: 'incisor' },
  '33': { type: 'canine' },
  '34': { type: 'premolar' },
  '35': { type: 'premolar' },
  '36': { type: 'molar' },
  '37': { type: 'molar' },
  '38': { type: 'molar' },
  // Cuadrante 4 (Inferior derecho)
  '41': { type: 'incisor' },
  '42': { type: 'incisor' },
  '43': { type: 'canine' },
  '44': { type: 'premolar' },
  '45': { type: 'premolar' },
  '46': { type: 'molar' },
  '47': { type: 'molar' },
  '48': { type: 'molar' },
};

const UPPER_RIGHT = ['18', '17', '16', '15', '14', '13', '12', '11'];
const UPPER_LEFT = ['21', '22', '23', '24', '25', '26', '27', '28'];
const LOWER_RIGHT = ['48', '47', '46', '45', '44', '43', '42', '41'];
const LOWER_LEFT = ['31', '32', '33', '34', '35', '36', '37', '38'];

interface ToothProps {
  number: string;
  isSelected: boolean;
  onClick: () => void;
  isLower?: boolean;
  isClickable: boolean;
}

function Tooth({ number, isSelected, onClick, isLower, isClickable }: ToothProps) {
  const config = TEETH_CONFIG[number];
  const path = TOOTH_PATHS[config.type];

  return (
    <div
      onClick={isClickable ? onClick : undefined}
      className={`
        relative flex flex-col items-center transition-all duration-200
        ${isClickable ? 'cursor-pointer' : 'cursor-default'}
        ${isSelected ? 'scale-105' : ''}
        group
      `}
      style={{ width: '48px' }}
    >
      {/* Número arriba para dientes inferiores */}
      {isLower && (
        <span
          className={`text-[11px] font-bold mb-1 transition-colors ${
            isSelected ? 'text-blue-500' : 'text-gray-400'
          }`}
        >
          {number}
        </span>
      )}

      {/* SVG del diente */}
      <div
        className={`
          w-12 h-14 flex items-center justify-center transition-all duration-300
          ${isClickable ? 'group-hover:-translate-y-0.5' : ''}
        `}
        style={{
          filter: isSelected
            ? 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.6))'
            : 'drop-shadow(0 2px 4px rgba(0,0,0,0.05))',
        }}
      >
        <svg
          className={`w-full h-full ${isLower ? 'rotate-180' : ''}`}
          viewBox="0 0 40 50"
        >
          <defs>
            <linearGradient id={`toothGrad-${number}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#FFFFFF', stopOpacity: 1 }} />
              <stop offset="60%" style={{ stopColor: '#FDFCF0', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#F3EFE0', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <path
            d={path}
            fill={`url(#toothGrad-${number})`}
            stroke={isSelected ? '#3B82F6' : '#E2E8F0'}
            strokeWidth={isSelected ? 2 : 1}
          />
          {/* Indicador de selección */}
          {isSelected && (
            <circle
              cx="20"
              cy="25"
              r="5"
              fill="#3B82F6"
              fillOpacity="0.3"
              stroke="#3B82F6"
              strokeWidth="1.5"
            />
          )}
        </svg>
      </div>

      {/* Número abajo para dientes superiores */}
      {!isLower && (
        <span
          className={`text-[11px] font-bold mt-1 transition-colors ${
            isSelected ? 'text-blue-500' : 'text-gray-400'
          }`}
        >
          {number}
        </span>
      )}
    </div>
  );
}

export function OdontogramAdult({
  selectedTeeth,
  onToothClick,
  selectionMode,
}: OdontogramAdultProps) {
  const isClickable = selectionMode === 'UNIT';

  return (
    <div className="w-full max-w-4xl space-y-6">
      {/* Arcada Superior */}
      <div className="flex justify-center gap-2">
        {/* Cuadrante 1 (Superior Derecho) */}
        <div className="flex gap-1.5 pr-4 border-r-2 border-gray-100 items-end">
          {UPPER_RIGHT.map((num) => (
            <Tooth
              key={num}
              number={num}
              isSelected={selectedTeeth.has(num)}
              onClick={() => onToothClick(num)}
              isClickable={isClickable}
            />
          ))}
        </div>

        {/* Cuadrante 2 (Superior Izquierdo) */}
        <div className="flex gap-1.5 pl-4 items-end">
          {UPPER_LEFT.map((num) => (
            <Tooth
              key={num}
              number={num}
              isSelected={selectedTeeth.has(num)}
              onClick={() => onToothClick(num)}
              isClickable={isClickable}
            />
          ))}
        </div>
      </div>

      {/* Línea media */}
      <div className="flex justify-center">
        <div className="h-px bg-gray-200 w-full max-w-3xl relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-6 py-1 border border-gray-100 rounded-full text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            Línea Media
          </div>
        </div>
      </div>

      {/* Arcada Inferior */}
      <div className="flex justify-center gap-2">
        {/* Cuadrante 4 (Inferior Derecho) */}
        <div className="flex gap-1.5 pr-4 border-r-2 border-gray-100 items-start">
          {LOWER_RIGHT.map((num) => (
            <Tooth
              key={num}
              number={num}
              isSelected={selectedTeeth.has(num)}
              onClick={() => onToothClick(num)}
              isLower
              isClickable={isClickable}
            />
          ))}
        </div>

        {/* Cuadrante 3 (Inferior Izquierdo) */}
        <div className="flex gap-1.5 pl-4 items-start">
          {LOWER_LEFT.map((num) => (
            <Tooth
              key={num}
              number={num}
              isSelected={selectedTeeth.has(num)}
              onClick={() => onToothClick(num)}
              isLower
              isClickable={isClickable}
            />
          ))}
        </div>
      </div>

      {/* Leyenda de cuadrantes */}
      <div className="flex justify-center gap-8 mt-4 text-xs text-gray-400">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center font-bold text-gray-500">
            Q1
          </div>
          <span>Superior Derecho</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center font-bold text-gray-500">
            Q2
          </div>
          <span>Superior Izquierdo</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center font-bold text-gray-500">
            Q4
          </div>
          <span>Inferior Derecho</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center font-bold text-gray-500">
            Q3
          </div>
          <span>Inferior Izquierdo</span>
        </div>
      </div>
    </div>
  );
}
